import Image from '@base/Image';
import styled from '@emotion/styled';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import Accordion from 'aquilla/core/molecules/Accordion';
import useCertificationCourseData from './useCertificationCourseData';

const Container = styled.div`
  display: grid;
  width: 100%;
  gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    row-gap: ${Spacings.SPACING_4B};
  }
`;

const Title = styled(Typography)``;

const MainEventsWrapper = styled.div`
  display: grid;
  gap: ${Spacings.SPACING_4B};
  grid-template-columns: 3fr 1fr;
  ${mediaQuery.uptoMobile} {
    grid-template-columns: 1fr;
  }
`;
const MainEventsSubWrapper = styled.div`
  display: grid;
  gap: ${Spacings.SPACING_5B};
  grid-template-columns: 1fr 1fr;
  ${mediaQuery.uptoMobile} {
    grid-template-columns: 1fr;
    gap: ${Spacings.SPACING_3B};
  }
`;
const Events = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_3B};
  }
`;

const EventLabel = styled(Typography)`
  font-weight: ${Fonts.LIGHT};
`;
const StyledButton = styled(Button)`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_6B};
  white-space: nowrap;
  width: 206px;
  height: 40px;
  border-radius: ${Spacings.SPACING_6};
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const ImageWrapper = styled.div`
  width: 20px;
  height: 20px;
  position: relative;
  ${mediaQuery.uptoMobile} {
    width: 24px;
    height: 24px;
  }
`;

export const DownloadIcon = () => {
  return (
    <ImageWrapper>
      <Image
        src="certificationCourses/download-arrow.svg"
        layout="fill"
        alt="Download brochure"
      />
    </ImageWrapper>
  );
};
const MainEvents = ({ mainEvents, handleDownloadBrochureCTA }) => {
  return (
    <MainEventsWrapper>
      <MainEventsSubWrapper>
        {mainEvents?.map((event) => {
          return (
            <Events key={event.uid}>
              <ImageWrapper>
                <Image src={event.benefitIcon} layout="fill" alt="benefits" />
              </ImageWrapper>
              <EventLabel variant="h6" color="var(--color-text-primary)">
                {event.benefitDescription}
              </EventLabel>
            </Events>
          );
        })}
      </MainEventsSubWrapper>
      <StyledButton
        onClick={handleDownloadBrochureCTA}
        size="large"
        variant="secondary"
        label="Download brochure"
        startIcon={<DownloadIcon />}
      />
    </MainEventsWrapper>
  );
};
const ProgrammeStructureWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

const HeaderWrapper = styled.div``;
const HeaderTitle = styled(Typography)``;
const SubText = styled(Typography)`
  margin-top: ${Spacings.SPACING_1B};
`;

const getSubText = (data) => {
  if (data?.topicChaptersCount && data?.topicDuration) {
    return `${data?.topicChaptersCount} chapters • ${data.topicDuration}`;
  }
  if (!data?.topicChaptersCount && data?.topicDuration) {
    return `${data.topicDuration}`;
  }
  if (data?.topicChaptersCount && !data?.topicDuration) {
    return `${data?.topicChaptersCount}`;
  }
  return '';
};
const StyledAccordion = styled(Accordion)`
  padding: ${Spacings.SPACING_6B};
  white-space: break-spaces;
  border: 1px solid var(--color-i-gray-14);
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_4B};
  }
`;
const Content = styled(Typography)`
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_2B};
  }
`;

const Header = ({ data }) => {
  return (
    <HeaderWrapper>
      <HeaderTitle variant="h5">{data?.topicName}</HeaderTitle>
      <SubText variant="p2" color="var(--color-text-secondary)">
        {getSubText(data)}
      </SubText>
    </HeaderWrapper>
  );
};
const ProgrammeStructure = ({ curriculumStructure }) => {
  return (
    <ProgrammeStructureWrapper>
      {curriculumStructure?.map((item, index) => {
        return (
          <StyledAccordion
            key={item.uid}
            title={<Header data={item} />}
            expanded={index === 0}
          >
            <Content>{item.topiCContent}</Content>
          </StyledAccordion>
        );
      })}
    </ProgrammeStructureWrapper>
  );
};
const Curriculum = ({ goalUID, handleDownloadBrochureCTA }) => {
  const { sections } = useCertificationCourseData(goalUID);
  const curriculum = sections?.curriculum ?? {};
  return (
    <Container>
      <Title variant="h3">
        {curriculum?.programmeCurriculumTitle || 'Curriculum'}
      </Title>
      <MainEvents
        mainEvents={curriculum.programmeCurriculumBenefits}
        handleDownloadBrochureCTA={handleDownloadBrochureCTA}
      />
      <ProgrammeStructure
        curriculumStructure={curriculum.programCurriculumStructure}
      />
    </Container>
  );
};

export default Curriculum;
